/**
 * رشته‌ی گفت‌وگو روی خط لوله‌ی مشاوره.
 *
 * ⚠️ این hook هیچ حالت دامنه‌ای نمی‌سازد؛ فقط `useConsultation` را به یک رشته‌ی
 * پیام ترجمه می‌کند. حالت مشاوره سمت سرور است و منبع حقیقت همان است.
 *
 * نگاشت پیام کاربر به تماس سرور:
 *   - رشته خالی است → `start`
 *   - آخرین پیام دستیار پرسش شفاف‌سازی است → پاسخ‌ها با `answer` می‌روند
 *   - برآورد آماده است → پیام تازه `refine` است (بازسازی با متن اصلاح‌شده)
 *
 * پیام‌ها فقط اضافه می‌شوند و بازنویسی نمی‌گردند؛ کاربر باید بتواند سیر اصلاح
 * برآورد را از بالا به پایین بخواند.
 */

import { useCallback, useMemo, useState } from 'react'

import { useConsultation } from '../useConsultation'

import type { ClarificationQuestion, ConsultationState, UserFeedback } from '@/lib/types'

export type ChatMessage =
  | { id: string; kind: 'user'; text: string }
  | { id: string; kind: 'note'; text: string }
  | {
      id: string
      kind: 'clarification'
      questions: ClarificationQuestion[]
      /** پاسخ داده شده؟ در آن حالت فرم فقط‌خواندنی می‌شود. */
      answered: boolean
      /**
       * پاسخ حفظ‌شده‌ی هر پرسش، به ترتیب پرسش‌ها. `null` یعنی این پرسش تازه
       * است و کاربر باید پاسخ بدهد.
       */
      prefill: (string | null)[]
      /** پاسخ‌هایی که حفظ شده‌اند اما این‌بار پرسیده نشده‌اند. */
      carriedCount: number
    }
  | { id: string; kind: 'verdict'; state: ConsultationState; isLatest: boolean }

interface Entry {
  id: string
  kind: 'user' | 'note' | 'clarification' | 'verdict'
  text: string
  questions: ClarificationQuestion[]
  state: ConsultationState | null
}

/**
 * پاسخ حفظ‌شده‌ی یک پرسش، اگر سرور آن را از تلاش قبل منتقل کرده باشد.
 *
 * تطبیق با متن پرسش انجام می‌شود چون سرور در `retained_answers` عیناً متن
 * پرسش‌های همین تلاش را می‌نویسد، نه متن تلاش قبل.
 */
const retainedFor = (state: ConsultationState, question: string): string | null => {
  const found = state.retained_answers.find((item) => item.question === question)
  if (found === undefined || found.is_unknown) {
    return null
  }
  return found.answer
}

/** پاسخ‌های حفظ‌شده به ترتیب پرسش‌های جاری. */
const prefillOf = (state: ConsultationState): (string | null)[] =>
  (state.analysis?.clarification_questions ?? []).map((item) =>
    retainedFor(state, item.question),
  )

export interface UseChatConsultation {
  messages: ChatMessage[]
  stage: ConsultationState['stage'] | null
  isBusy: boolean
  busyLabel: string
  send: (text: string) => void
  answerClarification: (answers: (string | null)[]) => void
  giveFeedback: (feedback: UserFeedback, correction: string) => void
  /** یک مشاوره‌ی بایگانی‌شده را به‌عنوان سابقه‌ی گفت‌وگو باز می‌کند. */
  resume: (state: ConsultationState) => void
  reset: () => void
}

/** شناسه‌ی یکتا برای هر پیام. ترتیب پیام‌ها از همین شمارنده می‌آید. */
let counter = 0
const nextId = (): string => {
  counter += 1
  return `m${String(counter)}`
}

const FAILED_NOTE =
  'برآورد به نتیجه نرسید. می‌توانید توصیف را کامل‌تر بنویسید و دوباره بفرستید.'

export const useChatConsultation = (): UseChatConsultation => {
  const consultation = useConsultation()
  const [entries, setEntries] = useState<Entry[]>([])
  /** متن آخرین توصیف کاربر. مبنای `refine` است. */
  const [lastCase, setLastCase] = useState('')

  const push = useCallback((entry: Omit<Entry, 'id'>) => {
    setEntries((current) => [...current, { ...entry, id: nextId() }])
  }, [])

  /** نتیجه‌ی یک تماس سرور را به پیام دستیار تبدیل می‌کند. */
  const absorb = useCallback(
    (state: ConsultationState | null) => {
      if (state === null) {
        return
      }
      if (state.stage === 'awaiting_clarification' && state.analysis !== null) {
        push({
          kind: 'clarification',
          text: '',
          questions: state.analysis.clarification_questions,
          state,
        })
        return
      }
      if (state.stage === 'failed') {
        push({ kind: 'note', text: state.error_message ?? FAILED_NOTE, questions: [], state })
        return
      }
      if (state.stage === 'completed' && state.advice !== null) {
        push({ kind: 'verdict', text: '', questions: [], state })
      }
    },
    [push],
  )

  const send = useCallback(
    (text: string) => {
      push({ kind: 'user', text, questions: [], state: null })
      const current = consultation.state

      if (current === null) {
        setLastCase(text)
        consultation.start(text, { onDone: absorb })
        return
      }

      if (current.stage === 'awaiting_clarification') {
        // کاربر به‌جای پر کردن فرم، پاسخش را در گفت‌وگو نوشته است. همان متن
        // پاسخ **نخستین پرسش بی‌پاسخ** می‌شود، نه همیشه پرسش اول: در بازسازی،
        // پرسش‌های پاسخ‌داده‌شده پیش‌پر می‌آیند و نوشتن روی آن‌ها یعنی پاک
        // کردن واقعیتی که کاربر قبلاً داده بود.
        const prefill = prefillOf(current)
        const target = prefill.findIndex((item) => item === null)
        const at = target === -1 ? 0 : target
        consultation.answer(
          prefill.map((item, index) => (index === at ? text : item)),
          { onDone: absorb },
        )
        return
      }

      // برآورد آماده است: پیام تازه یعنی بازسازی با فرض‌های عوض‌شده.
      //
      // ⚠️ متن اصلاح فقط یک بار به سرور می‌رود و آن هم در `note` است. پیش از
      // این همان متن هم به پرونده چسبانده می‌شد و هم در `note` می‌رفت، و چون
      // سرور خودش نکته را با عنوان مشخص به پرونده اضافه می‌کند، یک اصلاح دو
      // بار در ورودی مدل می‌نشست و وزنی بیش از واقعیت می‌گرفت.
      //
      // فهرست پاسخ خالی یعنی «ویرایشی روی پاسخ‌های قبلی ندارم»؛ سرور خودش
      // آن‌ها را بر اساس هویت پرسش منتقل می‌کند.
      consultation.refine(
        { revisedText: lastCase, note: text, answers: [] },
        { onDone: absorb },
      )
      // اصلاح در متن پرونده‌ی محلی می‌ماند تا اصلاح‌های دورهای بعد روی آن سوار
      // شوند و واقعیت‌های دورهای قبل گم نشوند.
      setLastCase(`${lastCase}\n\n[اصلاح] ${text}`)
    },
    [absorb, consultation, lastCase, push],
  )

  const answerClarification = useCallback(
    (answers: (string | null)[]) => {
      const written = answers.filter((item): item is string => item !== null && item !== '')
      push({
        kind: 'user',
        text: written.length === 0 ? 'اطلاعات بیشتری ندارم.' : written.join('\n'),
        questions: [],
        state: null,
      })
      consultation.answer(answers, { onDone: absorb })
    },
    [absorb, consultation, push],
  )

  /**
   * گفت‌وگو را با یک مشاوره‌ی بایگانی‌شده باز می‌کند.
   *
   * رشته با دو پیام ساخته می‌شود: پرسش اصلی کاربر و برآوردی که گرفته بود. پس
   * کاربر همان جایی را می‌بیند که رهایش کرده و پیام بعدی‌اش `refine` می‌شود.
   *
   * `base_user_input` مبنای `lastCase` است، نه `raw_user_input`: دومی نکته‌های
   * بازسازی‌های قبلی را هم دارد و استفاده از آن، متن پرونده را در هر ادامه
   * بلندتر می‌کرد.
   */
  const resume = useCallback(
    (state: ConsultationState) => {
      const text = state.base_user_input === '' ? state.raw_user_input : state.base_user_input
      setLastCase(text)
      consultation.adopt(state)
      setEntries([
        { id: nextId(), kind: 'user', text, questions: [], state: null },
        ...(state.advice === null
          ? []
          : [
              {
                id: nextId(),
                kind: 'verdict' as const,
                text: '',
                questions: [],
                state,
              },
            ]),
        {
          id: nextId(),
          kind: 'note' as const,
          text: 'این گفت‌وگو از آرشیو ادامه یافت. نکته یا اصلاح تازه‌ای بنویسید تا برآورد بازسازی شود.',
          questions: [],
          state: null,
        },
      ])
    },
    [consultation],
  )

  const reset = useCallback(() => {
    setEntries([])
    setLastCase('')
    consultation.reset()
  }, [consultation])

  const messages = useMemo<ChatMessage[]>(() => {
    const lastVerdictId = [...entries].reverse().find((entry) => entry.kind === 'verdict')?.id
    return entries.flatMap<ChatMessage>((entry) => {
      if (entry.kind === 'user' || entry.kind === 'note') {
        return [{ id: entry.id, kind: entry.kind, text: entry.text }]
      }
      if (entry.kind === 'clarification') {
        return [
          {
            id: entry.id,
            kind: 'clarification',
            questions: entry.questions,
            // پرسش‌ها پاسخ‌داده شمرده می‌شوند اگر مرحله‌ی جاری از شفاف‌سازی
            // گذشته باشد؛ همان یک منبع حقیقت سمت سرور.
            answered: consultation.state?.stage !== 'awaiting_clarification',
            prefill: entry.state === null ? [] : prefillOf(entry.state),
            carriedCount: entry.state?.carried_facts.length ?? 0,
          },
        ]
      }
      return entry.state === null
        ? []
        : [
            {
              id: entry.id,
              kind: 'verdict',
              state: entry.state,
              isLatest: entry.id === lastVerdictId,
            },
          ]
    })
  }, [entries, consultation.state?.stage])

  const busyLabel =
    consultation.state === null
      ? 'در حال بررسی پرونده و جست‌وجوی سابقه…'
      : consultation.state.stage === 'awaiting_clarification'
        ? 'در حال ساختن برآورد…'
        : 'در حال بازسازی برآورد با فرض‌های تازه…'

  return {
    messages,
    stage: consultation.state?.stage ?? null,
    isBusy: consultation.isBusy,
    busyLabel,
    send,
    answerClarification,
    giveFeedback: consultation.giveFeedback,
    resume,
    reset,
  }
}
